import { createContext, useContext } from "react";

/** Whether an item should render its text label beside its icon. */
export type WorkspaceControlLabels = "visible" | "hidden";

/**
 * Where an item is being rendered. `pill` is the floating group itself;
 * `overflow` means the group has run out of room and the item should render as a
 * menu row instead.
 */
export type WorkspaceControlStage = "pill" | "overflow";

/** Whether a module should use its compact two-row phone composition. */
export type WorkspaceControlLayout = "single-row" | "stacked-phone";

export interface WorkspaceControlDensity {
  readonly labels: WorkspaceControlLabels;
  readonly stage: WorkspaceControlStage;
  readonly layout: WorkspaceControlLayout;
  /** Number of module-owned fold thresholds crossed at the settled width. */
  readonly foldLevel: number;
  /**
   * Width of the group's containing work area, after any side rail has taken
   * its share. Consumers can use this for progressive overflow before the
   * phone-width all-or-nothing stage.
   */
  readonly availableWidth: number;
}

const DEFAULT_DENSITY: WorkspaceControlDensity = {
  labels: "visible",
  stage: "pill",
  layout: "single-row",
  foldLevel: 0,
  availableWidth: Number.POSITIVE_INFINITY,
};

const WorkspaceControlDensityContext =
  createContext<WorkspaceControlDensity>(DEFAULT_DENSITY);

/** Internal — `WorkspaceControlGroup` is the only intended provider. */
export const WorkspaceControlDensityProvider =
  WorkspaceControlDensityContext.Provider;

/**
 * How much room the surrounding workspace control group has, so an item can
 * render itself accordingly.
 *
 * **Why a signal rather than a descriptor array.** `packages/ui/CLAUDE.md`
 * invariant 2 is explicit — the library provides placement and styling, never
 * the content's logic — and the controls this group has to carry cannot be
 * expressed as data: Hoshin's `Previous` is a bare `<button>` with its own
 * classes rather than a `Button`; its `Advance` puts the icon AFTER the label
 * while VSM's toolbar buttons put it first, and takes its label from module
 * data; a disabled `Advance` needs a SIBLING `sr-only` node for
 * `aria-describedby` to point at, so the group would have to emit two elements
 * per entry; the phase pill is not interactive at all; and `Follow facilitator`
 * is a toggle with two different labels and a pressed state.
 *
 * But slotted children alone cannot collapse either: the group cannot strip a
 * label out of a node it did not build, and folding into an overflow menu means
 * the SAME control renders as a pill button in one stage and a menu row in
 * another. One opaque node cannot be both.
 *
 * So the group owns placement, the collapse policy, traversal and its own
 * measurement, and publishes the stage; each item reads it and renders itself.
 * The trade is deliberate and worth stating: the group promises the SIGNAL, not
 * the rendering. An item that ignores this hook will not collapse, and no test
 * of the group can prove otherwise — which is why the group's own spec asserts
 * the signal flips at the right breakpoints and that a reference item honours
 * it.
 *
 * Outside a group it returns the roomiest answer, so an item rendered on its own
 * (a story, a test, a module that has not adopted the group yet) looks normal.
 */
export function useWorkspaceControlDensity(): WorkspaceControlDensity {
  return useContext(WorkspaceControlDensityContext);
}
